import { capitalize } from 'lodash-es';
import { createElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ArrowDropDown,
  ArrowDropUp,
  CheckBox,
  CheckBoxOutlineBlank,
  VFlex,
  VIconButton,
  VStack,
  useFlag,
} from '@/package';

import type { TermItem } from './type';

type PrivancyTermItemProps = {
  termItem: TermItem & { checked: boolean };
  onTermAgreeChecked(checked: boolean): void;
};

const PrivacyTermItem = ({
  termItem,
  onTermAgreeChecked,
}: PrivancyTermItemProps) => {
  const { t } = useTranslation();
  const {
    value: checked,
    toggle: toggleCheck,
    setFalse: uncheck,
    setTrue: check,
  } = useFlag(termItem.checked);
  const { value: expanded, toggle: toggleExpand } = useFlag(false);

  useEffect(() => {
    onTermAgreeChecked(checked);
  }, [checked]);

  useEffect(() => {
    termItem.checked ? check() : uncheck();
  }, [termItem.checked]);

  return (
    <VStack className="py-1" gap={0.125}>
      <VFlex gap={0.25} items="center">
        <div
          className="cursor-pointer text-primary-500"
          role="checkbox"
          onClick={toggleCheck}
        >
          <input
            id={termItem.name}
            className="hidden"
            type="checkbox"
            defaultChecked={checked}
          />
          {createElement(!checked ? CheckBoxOutlineBlank : CheckBox, {
            className: 'size-4',
          })}
        </div>

        <div
          className="flex-1 text-body-md text-gray-600"
          role="presentation"
          aria-label="term title"
        >
          <label
            className="flex cursor-pointer select-none"
            htmlFor={termItem.name}
          >
            {t(termItem.title)}&nbsp;(
            {capitalize(
              termItem.required
                ? t('common.required.decayer')
                : t('common.optional.demiditone'),
            )}
            )
          </label>
        </div>

        <VIconButton
          Icon={!expanded ? ArrowDropDown : ArrowDropUp}
          size="sm"
          type="button"
          variant="roundPrimary"
          onClick={toggleExpand}
        />
      </VFlex>

      {expanded && (
        <div
          className="pr-1 pl-5 text-body-sm whitespace-pre-line text-gray-500"
          dangerouslySetInnerHTML={{
            __html: t(termItem.content),
          }}
        />
      )}
    </VStack>
  );
};

PrivacyTermItem.displayName = 'PrivacyTermItem';

export default PrivacyTermItem;
